import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/frontend/components/ui/dialog";
import { Button } from "@/frontend/components/ui/button";
import { Checkbox } from "@/frontend/components/ui/checkbox";
import { Label } from "@/frontend/components/ui/label";
import { Alert, AlertDescription } from "@/frontend/components/ui/alert";
import { Loader2, FileText, AlertCircle } from "lucide-react";
import { Contract, Regulation, RegulationAcceptance } from "@/shared/types";

interface RegulationAcceptanceDialogProps {
  regulation: Regulation;
  contract: Contract;
  onAccept: () => Promise<void>;
  isAccepting: boolean;
  previousAcceptance?: RegulationAcceptance | null;
  isReAcceptance?: boolean;
}

export const RegulationAcceptanceDialog = ({
  regulation,
  contract,
  onAccept,
  isAccepting,
  previousAcceptance,
  isReAcceptance = false,
}: RegulationAcceptanceDialogProps) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleAccept = async () => {
    if (!acknowledged) return;
    await onAccept();
  };

  const previousAcceptanceDate = previousAcceptance?.acceptedAt
    ? new Date(previousAcceptance.acceptedAt.seconds * 1000).toLocaleDateString()
    : null;

  // Surface a one-line note when the rental contract file has been replaced
  // since the student's last acknowledgement.
  const contractWasUpdated =
    isReAcceptance &&
    previousAcceptance?.contractFileVersion !== undefined &&
    previousAcceptance.contractFileVersion !== contract.contractFilePath;

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-3xl max-h-[90vh] flex flex-col"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {isReAcceptance
              ? "Updated Documents"
              : "Residence Regulations & Rental Contract"}
          </DialogTitle>
          <DialogDescription>
            {isReAcceptance
              ? "Please review the updated documents below and confirm your acknowledgement to continue."
              : "Please review both documents below and confirm your acknowledgement to continue."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 flex-1 overflow-y-auto">
          {isReAcceptance && previousAcceptance && (
            <Alert variant="default" className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Documents Updated:</strong> You previously acknowledged
                regulation version {previousAcceptance.regulationVersion} on{" "}
                {previousAcceptanceDate}. The regulations are now at version{" "}
                {regulation.version}.
                {contractWasUpdated && (
                  <> The rental contract has also been updated since your last acknowledgement.</>
                )}{" "}
                Please review the documents below and acknowledge again to
                continue.
              </AlertDescription>
            </Alert>
          )}

          {/* Residence regulations PDF */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">
              Residence Regulations (Version {regulation.version})
            </h3>
            <div className="border rounded-lg overflow-hidden bg-muted">
              <div className="h-[500px] w-full">
                <iframe
                  src={regulation.fileUrl}
                  className="w-full h-full"
                  title="Residence Regulations"
                />
              </div>
            </div>
          </div>

          {/* Rental contract PDF */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Rental Contract</h3>
            <div className="border rounded-lg overflow-hidden bg-muted">
              <div className="h-[500px] w-full">
                <iframe
                  src={contract.contractFileUrl}
                  className="w-full h-full"
                  title="Rental Contract"
                />
              </div>
            </div>
          </div>

          {/* Single combined acknowledgement */}
          <div className="space-y-3 bg-muted p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <Checkbox
                id="combined-acknowledgement"
                checked={acknowledged}
                onCheckedChange={(checked) => setAcknowledged(checked === true)}
                disabled={isAccepting}
              />
              <Label
                htmlFor="combined-acknowledgement"
                className="text-sm font-medium leading-relaxed cursor-pointer"
              >
                I have read and accept the residence regulations and
                acknowledge the rental contract.
              </Label>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <FileText className="inline h-4 w-4 mr-1" />
              You must acknowledge both documents to access your student portal.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
          <Button
            onClick={handleAccept}
            disabled={!acknowledged || isAccepting}
            className="w-full sm:w-auto"
          >
            {isAccepting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Recording Acceptance...
              </>
            ) : (
              "Accept and Continue"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
